import { faCheck, faChevronLeft, faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Divider, Typography } from "@mui/material"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { messageSVC } from "message/service"
import React from "react"
import { connect } from "react-redux"
import { FlexBox } from "ui/components/FlexBox"
import { InfoRow } from "ui/components/InfoRow"
import { addImportant } from "ui/tools/common"
import { COUNTRIES_WITCH_REQUIRE_STATE, IUserSignUpState } from "user/model"
import { createSignUpUser, decrementSignUpStep } from "user/tools/signup"

interface ISignUpWizardVerificationProps {
  signup: IUserSignUpState
}

const SignUpWizardVerificationFn = React.memo(
  ({ signup: { userForm, agencyForm } }: ISignUpWizardVerificationProps) => {
    React.useEffect(() => {
      const script = document.createElement("script")
      script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_TOKEN}`
      script.async = true
      document.body.appendChild(script)
    }, [])
    return (
      <FlexBox
        vertical
        id={"signup-form-verification"}
        sx={{
          width: "100%",
          padding: ".5rem",
          maxWidth: "100%",
          height: "100%",
          minHeight: "100%",
          maxHeight: "100%"
        }}>
        <FlexBox
          vertical
          gap
          fullWidth
          jc="flex-start"
          sx={{
            "overflow": "auto",
            "maxWidth": "100%",
            "height": "calc(100% - 5rem)",
            "minHeight": "calc(100% - 5rem)",
            "maxHeight": "calc(100% - 5rem)",
            "pt": ".5rem",
            "& > *": {
              width: "100%"
            },
            "& > div > p": {
              width: "48%"
            }
          }}>
          <InfoRow
            id="user-name"
            title={translate("label.user")}
            value={`${userForm.data.firstname} ${userForm.data.lastname}`}
          />
          <InfoRow id="email" title={translate("label.email")} value={userForm.data.email} />
          <InfoRow id="phone" title={translate("label.phone")} value={userForm.data.phone} />
          <InfoRow
            id="language"
            title={translate("label.language")}
            value={translate(`label.language.${userForm.data.language_id}`)}
          />
          <Divider />
          <InfoRow id="agency-name" title={translate("label.agency")} value={agencyForm.data.name} />
          <InfoRow id="agency-currency" title={translate("label.currency")} value={agencyForm.data.currency_id} />
          <InfoRow id="agency-country" title={translate("label.country")} value={agencyForm.data.country} />
          {COUNTRIES_WITCH_REQUIRE_STATE.includes(agencyForm.data.country) && (
            <InfoRow
              id="agency-state"
              title={translate(agencyForm.data.state === "US" ? "label.country" : "label.province")}
              value={agencyForm.data.state}
            />
          )}
          <InfoRow
            id="agency-language"
            title={translate("label.language")}
            value={translate(`label.language.${agencyForm.data.language_id}`)}
          />
          <Divider />
          <FlexBox gap sx={{ "& .icon": { color: theme => theme.palette.primary.main } }}>
            <FontAwesomeIcon icon={faTriangleExclamation} size="2x" className="icon" />
            <Typography color="textPrimary" sx={{ width: addImportant("100%"), textAlign: "justify" }}>
              {translate("user.signup.help")}
            </Typography>
          </FlexBox>
        </FlexBox>
        <FlexBox gap fullWidth ai="end" jc="space-between" sx={{ mt: "2rem", width: "100%" }}>
          <Button
            id="signup-previous"
            variant="outlined"
            onClick={decrementSignUpStep}
            startIcon={<FontAwesomeIcon icon={faChevronLeft} className="icon-previous" />}>
            {translate("label.step.previous")}
          </Button>
          <Button
            id="signup-save"
            variant="contained"
            onClick={event => {
              messageSVC.confirmMessage({
                title: translate("user.signup.confirm.title"),
                message: translate("user.signup.confirm.message"),
                addGenericConfirmMessage: true,
                onSuccess: () => {
                  grecaptcha.ready(() => {
                    grecaptcha
                      .execute(RECAPTCHA_TOKEN, { action: "submit" })
                      .then((token: string) => {
                        createSignUpUser(token)
                      })
                      .catch((error: any) => {
                        console.error("Unable to get recaptcha token", error)
                        messageSVC.error(translate("user.login.error.unexpected"))
                      })
                  })
                }
              })
            }}
            startIcon={<FontAwesomeIcon icon={faCheck} className="icon-next" />}>
            {translate("button.continue")}
          </Button>
        </FlexBox>
      </FlexBox>
    )
  }
)

export const SignUpWizardVerification = connect(
  (state: IRootState): ISignUpWizardVerificationProps => ({
    signup: state.user.signup
  })
)(SignUpWizardVerificationFn)
